import { Badge } from '@risksmart-app/atomic-ui';
import type { OrganisationWebhookDeliveryStatus } from '@risksmart-app/domain/src/types/consts/organisation-webhook-delivery-status';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `pending` is a UI-only sentinel meaning "no delivery attempts logged yet" —
 * it is not a persisted delivery status. The subscription list passes it when
 * a webhook has no delivery rows; the delivery-logs modal never does.
 */
export type WebhookDeliveryBadgeStatus =
  | OrganisationWebhookDeliveryStatus
  | 'pending';

/**
 * Maps a delivery status to an atomic-ui Badge variant, mirroring the existing
 * delivery-logs pattern: success → `success`, failed → `destructive`,
 * everything else (pending / cancelled) → `neutral`.
 */
const variantFor = (status: string): 'destructive' | 'neutral' | 'success' => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'destructive';
    default:
      return 'neutral';
  }
};

interface Props {
  /**
   * A persisted delivery status (`success` / `failed` / `cancelled`) or the
   * `pending` sentinel. Typed as `string` because GraphQL surfaces the column
   * as a plain string; unrecognised values fall back to a neutral badge.
   */
  status: WebhookDeliveryBadgeStatus | string;
}

/**
 * Shared status badge for a webhook delivery status. Used by both the
 * subscription list ("last delivery" column) and the delivery-logs modal so
 * the two render identically from one source.
 */
const WebhookDeliveryStatusBadge: FC<Props> = ({ status }) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'webhooks.deliveryLogs.status',
  });

  // Literal keys keep `t` typed to a string; an unknown value falls back to
  // the raw status rather than throwing.
  const label = ((): string => {
    switch (status) {
      case 'success':
        return t('success');
      case 'failed':
        return t('failed');
      case 'cancelled':
        return t('cancelled');
      case 'pending':
        return t('pending');
      default:
        return status;
    }
  })();

  return <Badge variant={variantFor(status)}>{label}</Badge>;
};

export default WebhookDeliveryStatusBadge;
